import { useCallback, useMemo, useRef, useState } from 'react';
import PageHeader from './components/PageHeader';
import Pagination from './components/Pagination';
import IconButton from './components/IconButton';
import EditActionIcon from './components/EditActionIcon';
import SortableTableHeader from './components/SortableTableHeader';
import { ListViewIcon, TileViewIcon } from './components/ViewToggleIcons';
import useFacilitiesPageResults from './hooks/useFacilitiesPageResults';
import { useUrlFilters, useUrlParam } from './hooks/urlParams';
import { getNextSortCriterion, type FieldSortCriterionValue } from './utils/tableSort';
import { getAvailableActions, getWriteActions } from './utils/permissionCatalog';
import { checkPermissionActions } from './utils/permissions';

export default function FacilitiesPage({
    organizationId,
  currentUser,
  onImport,
  onEdit,
  showHeader = true,
  showImportButton = true,
  showCounts = true,
  showViewToggle = true,
  defaultView = 'tiles'
}) {
  const actions = getAvailableActions(currentUser);
  const canWriteFacilities = checkPermissionActions(actions, getWriteActions('Facilities'));
  const canImportFacilities =
    checkPermissionActions(actions, getWriteActions('Facilities')) &&
    checkPermissionActions(actions, getWriteActions('Imports'));
  const [view, setView] = useUrlParam<string>('view', defaultView);
  const descendingFirstFields = useRef(new Set<string>());
    const [page, setPage] = useState(0);
    const pageSize = 12;

    const [filters, setFilters] = useUrlFilters<{
        name: string;
        marketId: string;
    }>({
        name: { default: '', type: 'string' },
        marketId: { default: 'all', type: 'string' },
    });

    const [sort, setSort] = useState<FieldSortCriterionValue>({
      fieldName: 'name',
      isAscending: true,
    });
    const [resultsOrganizationId, setResultsOrganizationId] = useState(organizationId);

    if (resultsOrganizationId !== organizationId) {
      setResultsOrganizationId(organizationId);
      setPage(0);
    }

    const { results, markets } = useFacilitiesPageResults({ organizationId, page, pageSize, filters, sort });
    const marketNamesById = useMemo(() => {
      const names = new Map<string, string>();
      for (const market of markets || []) {
        if (market?.id == null) continue;
        const id = String(market.id);
        if (!names.has(id)) names.set(id, market.name || '');
      }
      return names;
    }, [markets]);

    const toggleSortDirection = useCallback((fieldName: string) => {
      setSort((current) => getNextSortCriterion(current, fieldName, descendingFirstFields.current));
      setPage(0);
    }, []);

  const applyFilters = (updater) => {
    setPage(0);
    setFilters(updater);
  };

  return (
    <div className="stack" data-testid="page-facilities">
      {showHeader && (
        <PageHeader
          title="Facilities"
          subtitle="Facility List"
        />
      )}

      <div className="card filter-card">
        <div className="filter-bar">
          <div className="filter-left">
            <input
              className="input filter-search"
              placeholder="Search name, org, license"
              value={filters.name}
              onChange={(e) => applyFilters((current) => ({ ...current, name: e.target.value }))}
              data-testid="facilities-search"
          />
            <select
                className="select"
                value={filters.marketId}
                onChange={(e) => applyFilters((current) => ({ ...current, marketId: e.target.value }))}
                style={{ minWidth: 200 }}
                data-testid="facilities-filter-market"
            >
                <option value="all">All facility licenses</option>
                {[...markets]
                  .sort((a, b) => (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' }))
                  .map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                ))}
            </select>

          </div>
          <div className="filter-right">
            {showCounts && (
            <div className="hero-counts" aria-label="counts">
                <span>Total: {results.total}</span>
                <span>Shown: {results.data.length}</span>
              </div>
            )}
            {showViewToggle && (
              <div className="segmented view-toggle">
                <button
                  type="button"
                  className={`segment view-toggle-btn ${view === 'tiles' ? 'active' : ''}`}
                  onClick={() => setView('tiles')}
                  aria-label="Tile view"
                  title="Tile view"
                >
                  <TileViewIcon />
                </button>
                <button
                  type="button"
                  className={`segment view-toggle-btn ${view === 'list' ? 'active' : ''}`}
                  onClick={() => setView('list')}
                  aria-label="List view"
                  title="List view"
                >
                  <ListViewIcon />
                </button>
              </div>
            )}
            {showImportButton && canImportFacilities && (
              <button className="button cta" onClick={onImport}>
                <span className="plus" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.33301 8H12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8 3.33301V12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                Import Facility
              </button>
            )}
          </div>
        </div>
      </div>

      {view === 'list' ? (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <SortableTableHeader label="Name" fieldName="name" sort={sort} onSort={toggleSortDirection} />
                <SortableTableHeader label="License" fieldName="license" sort={sort} onSort={toggleSortDirection} />
                <th>Market</th>
                <th className="col-actions" aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {results.data.map((f) => (
                <tr key={f.id}>
                  <td>{f.name}</td>
                  <td>{f.license}</td>
                  <td>{marketNamesById.get(String(f.marketId)) || '—'}</td>
                  <td className="col-actions">
                    {canWriteFacilities && <IconButton title="Edit" ariaLabel={`Edit ${f.name || 'facility'}`} onClick={() => onEdit && onEdit(f)}>
                      <EditActionIcon />
                    </IconButton>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid two-col">
          {results.data.map((f) => (
            <div key={f.id} className="card" style={{ margin: 0 }}>
              <div style={{ fontWeight: 700 }}>{f.name}</div>
              <div className="muted">{f.org}</div>
              <div className="muted">License: {f.license}</div>
              <div className="card-actions" style={{ marginTop: 12 }}>
                {canWriteFacilities && <IconButton title="Edit" ariaLabel={`Edit ${f.name || 'facility'}`} onClick={() => onEdit && onEdit(f)}>
                  <EditActionIcon />
                </IconButton>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination
              page={results.page}
              totalPages={Math.ceil(results.total / results.pageSize)}
              onPrev={() => setPage(results.page - 1)}
              onNext={() => setPage(results.page + 1)}
              format="of"
              prevLabel="◀"
              nextLabel="▶"
      />
    </div>
  );
}
